/**
 * Wer den Takt angibt, wenn etwas läuft.
 *
 * Die Vorschau hatte zwei Uhren: die Zeitleiste zählte in Wanduhr-Zeit
 * weiter, das Medienelement lief mit seiner eigenen. Läuft der Dekoder
 * langsamer als die Wanduhr -- auf dem Telefon der Normalfall, sobald das
 * Gerät drosselt --, laufen beide auseinander, und die Vorschau glich den
 * Abstand mit einem Sprung aus. Ein Sprung setzt auf iOS die Dekodierung neu
 * auf: das Bild steht, der Abstand wächst wieder, es springt erneut.
 *
 * Deshalb gibt jetzt das Medienelement den Takt an, und alle anderen folgen
 * ihm -- nicht mit Sprüngen, sondern indem sie ein wenig schneller oder
 * langsamer laufen, bis der Abstand aufgebraucht ist.
 */
/** Wo auf der Zeitleiste ein Clip steht, der gerade bei `mediaTime` ist. */
export function zeitleistenZeit(clip, mediaTime) {
    const speed = clip.speed > 0 ? clip.speed : 1;
    return clip.start + (mediaTime - clip.sourceIn) / speed;
}
/**
 * Wie ein folgendes Element den Abstand zum Takt aufholt. Kleine Abstände
 * werden über das Tempo eingefangen (höchstens `max`, voreingestellt fünf
 * Prozent -- darüber hört man die Tonhöhe wandern); erst ab `sprung` Sekunden
 * lohnt der Sprung, der auf dem Telefon teuer ist.
 */
export function angleichen(soll, ist, speed = 1, { sprung = 0.5, max = 0.05, verstaerkung = 0.5, totzone = 0.08, } = {}) {
    const grund = speed > 0 ? speed : 1;
    const abstand = soll - ist;
    if (Math.abs(abstand) > sprung)
        return { rate: grund, springen: true };
    // Innerhalb der Totzone bleibt das Tempo, wie es ist. Ohne sie wird bei
    // jedem Bild ein anderer Wert gesetzt -- und ein Tonelement, dessen Tempo
    // sechzigmal je Sekunde minimal wackelt, klingt auf dem Telefon zerhackt.
    // (Saschas Fund vom 6. September: „mein Voiceover hakt ziemlich“.)
    if (Math.abs(abstand) < totzone)
        return { rate: grund, springen: false };
    const korrektur = Math.max(-max, Math.min(max, abstand * verstaerkung));
    return { rate: grund * (1 + korrektur), springen: false };
}
/**
 * Derselbe Abgleich, aber ruhig -- für Videoelemente (7.69i).
 *
 * `angleichen` stellt das Tempo proportional zum Abstand: je näher, desto
 * weniger. Das ist eine Treppe -- bei jedem Durchlauf ein anderer Wert,
 * bis der Abstand unter der Totzone liegt, und dann noch einmal zurück auf
 * das Grundtempo. Für ein Tonelement ist das erträglich. Für ein Video auf
 * dem iPhone ist jeder Tempowechsel ein Ruckler (Sascha, 8. September, „Anno
 * Domini“: das Video auf der Ebene ruckelt, das auf der Basisspur nicht --
 * gemessen: sechs Tempowechsel in anderthalb Sekunden).
 *
 * Deshalb hier mit Hysterese und festem Tempo: korrigiert wird erst ab
 * `anfang` Abstand, dann mit genau *einem* Tempo (`max`), und zwar so lange,
 * bis der Abstand unter `ende` liegt. Ein Ausreisser kostet damit zwei
 * Tempowechsel -- einen hin, einen zurück -- statt sechs. Ob gerade
 * korrigiert wird, sagt das Element selbst: sein Tempo weicht vom Grundtempo
 * ab.
 */
export function angleichenRuhig(soll, ist, speed = 1, aktuellesTempo = speed, { sprung = 0.5, max = 0.04, anfang = 0.15, ende = 0.03, geduld = 0, ueberSeit = Infinity, } = {}) {
    const grund = speed > 0 ? speed : 1;
    const abstand = soll - ist;
    if (Math.abs(abstand) > sprung)
        return { rate: grund, springen: true };
    const korrigiert = Math.abs(aktuellesTempo / grund - 1) > 0.001;
    const richtung = abstand > 0 ? 1 : -1;
    if (korrigiert) {
        // Weiter in dieselbe Richtung, bis es reicht -- und kein Umschwenken:
        // hat sich das Vorzeichen gedreht, ist der Abstand ohnehin klein.
        return { rate: Math.abs(abstand) < ende ? grund : grund * (1 + richtung * max), springen: false };
    }
    // **Erst wenn der Abstand bleibt** (7.191): unter Last springt die Zeit,
    // gegen die verglichen wird, für ein paar Bilder -- ein echter Rückstand
    // bleibt, ein Ausreisser ist nach Zehntelsekunden wieder weg. Gegen den
    // Ausreisser nachzustellen hiesse zweimal Tempo wechseln, also zweimal
    // ruckeln, für nichts.
    const lange = ueberSeit >= geduld;
    return { rate: Math.abs(abstand) > anfang && lange ? grund * (1 + richtung * max) : grund, springen: false };
}
export function taktLaeuft(stand, medienzeit, jetzt, geduld = 400) {
    if (stand.zuletzt === null)
        return { laeuft: true, stand: { zuletzt: medienzeit, seit: jetzt } };
    // Ein Sprung zurück (Schleife, Suchen) zählt als Bewegung.
    if (Math.abs(medienzeit - stand.zuletzt) > 1e-4) {
        return { laeuft: true, stand: { zuletzt: medienzeit, seit: jetzt } };
    }
    return { laeuft: jetzt - stand.seit < geduld, stand };
}
/**
 * Welche Zeit die Zeitleiste als Nächstes zeigt.
 *
 * Die Wanduhr ist der Rückfall, die Medienuhr der Takt — aber nur, solange sie
 * plausibel ist. Eine Medienzeit, die weit neben dem steht, was gerade laufen
 * müsste (ein Element, das noch die Stelle von vorhin hält, ein Sprung durch
 * ein Nachladen), darf den Kopf nicht mitreißen: im schlimmsten Fall springt
 * er ans Ende und die Wiedergabe hält sofort an, als wäre nichts abzuspielen.
 *
 * `null` als `vomMedium` heißt schlicht: niemand taktet, es gilt die Wanduhr.
 */
export function naechsteZeit(jetzigeZeit, delta, vomMedium, toleranz = 1) {
    const wanduhr = jetzigeZeit + delta;
    if (vomMedium === null || !Number.isFinite(vomMedium))
        return wanduhr;
    // Rückwärts darf der Takt nur ein Stück: eine Medienzeit weit hinter dem
    // Kopf ist ein Element, das noch nicht so weit ist, kein Rückspulen.
    if (Math.abs(vomMedium - wanduhr) > toleranz)
        return wanduhr;
    // Und innerhalb der Toleranz rückwärts gar nicht (7.142). Hier stand
    // `return vomMedium`, und damit lief der Kopf beim Abspielen zurück: ein
    // Element, das gerade nachlädt, hält eine Medienzeit hinter dem Kopf, sie
    // liegt dicht genug, um geglaubt zu werden -- und der Kopf ging mit. Im Bau
    // #314 gemessen: erste Bewegung bei 0,07 s, nach 1,2 s bei 0,00 s.
    //
    // Wer dem Bild folgt und dem Bild voraus ist, *wartet* -- er spult nicht
    // zurück. Stillstehen sieht niemand, ein Sprung nach hinten schon, und er
    // kostet die Zeit doppelt: einmal beim Zurückgehen, einmal beim
    // Wiederholen. Auflösen tut sich das von selbst, sobald das Element den
    // Kopf erreicht; kommt es gar nicht voran, gibt `taktLaeuft` nach 400 ms
    // an die Wanduhr ab.
    return Math.max(jetzigeZeit, vomMedium);
}
export function darfAnlaufen(stand, jetzt, geduld = 2000, ruhe = 1000) {
    if (!stand.paused)
        return false;
    if (stand.abgewiesen != null && jetzt - stand.abgewiesen < ruhe)
        return false;
    if (stand.seit === null)
        return true;
    return jetzt - stand.seit >= geduld;
}
/**
 * Was anzufangen und was anzuhalten ist.
 *
 * Die Regel der Tonkette (7.39), rein gerechnet, damit sie sich prüfen lässt,
 * ohne einen Browser zu starten: was klingen soll und noch nicht klingt, fängt
 * an; was klingt und nicht mehr soll, hört auf. Und was schon richtig läuft,
 * bleibt in Ruhe — eine Quelle neu zu starten, die schon spielt, wäre genau
 * das Stottern, das wir loswerden wollten.
 */
export function anpassen(ist, soll) {
    const istMenge = new Set(ist);
    const sollMenge = new Set(soll);
    return {
        starten: [...sollMenge].filter((id) => !istMenge.has(id)),
        stoppen: [...istMenge].filter((id) => !sollMenge.has(id)),
    };
}
export function sprungErlaubt(stand, jetzt, ruhe = 1500) {
    if (stand.suchend)
        return false;
    if (stand.bereit < 3)
        return false;
    if (stand.letzterSprung !== null && jetzt - stand.letzterSprung < ruhe)
        return false;
    return true;
}
/**
 * Wohin gesprungen wird: nicht dorthin, wo der Kopf *jetzt* ist, sondern
 * dorthin, wo er sein wird, wenn das Suchen fertig ist. Sonst steht das
 * Element nach jedem Sprung um die Suchdauer hinten -- und springt wieder.
 * Die Suchdauer ist die zuletzt gemessene; über zwei Sekunden traut ihr
 * niemand mehr.
 */
export function sprungZiel(jetzt, speed, suchdauerMs) {
    const grund = speed > 0 ? speed : 1;
    const dauer = Number.isFinite(suchdauerMs) ? Math.min(2000, Math.max(0, suchdauerMs)) : 0;
    return jetzt + (grund * dauer) / 1000;
}
/** Die gemessene Suchdauer, geglättet: halb die alte, halb die neue. */
export function suchdauerNeu(bisher, gemessenMs) {
    const neu = Math.max(0, gemessenMs);
    return bisher === null ? neu : bisher * 0.5 + neu * 0.5;
}
//# sourceMappingURL=takt.js.map